/**
 * `defineShell` — a route opts into the document shell (partial prerendering,
 * COM-482) by exporting one:
 *
 * ```ts
 * // app/routes/index.tsx
 * export const shell = defineShell({
 *   fingerprint: ["type", "pageType", "data"],
 *   perRequestKeys: { data: ["ssrKey"] },
 * });
 * ```
 *
 * A shell is the route's document rendered once from a synthetic cookieless
 * guest request with every `PprHole` left open, cached per build + site +
 * host + path, and streamed to eligible requests before any loader runs; the
 * request's own render then fills only the holes. The Worker seam finds the
 * config by matching the request URL against the server build's route
 * manifest and reading the deepest matched route module's `shell` export
 * (`cloudflare/ppr.ts` `resolvePprShellRoute`); a route without one never
 * takes the shell path. Full contract: `docs/ppr-shell.md`.
 *
 * The hole rule: everything the route renders OUTSIDE a `PprHole` is shared
 * by every guest and must be a pure function of the loader fields listed in
 * `fingerprint` (plus the root/layout chrome payload every shell route
 * shares, digested whole minus `SHELL_CHROME_EXCLUDED_FIELDS` in
 * `ppr-fingerprint.server.ts`). Anything per-shopper or per-request —
 * sign-in state, basket, vid, traits, nonce-derived values, the query
 * string — renders inside a hole, and stays OUT of `fingerprint` (listing
 * one would make every hit stale; omitting a field that renders outside a
 * hole would hydrate the shell against the wrong data).
 *
 * This module ships in the client bundle with the route (React Router keeps
 * non-server route exports), so it must stay dependency-free and tiny.
 */

/**
 * What a route's `vary` hook sees of the visitor at the Worker seam — before
 * any loader runs, resolved from the request alone (cookies, the warm
 * campaign manifest). Deliberately small: every field is cheap to compute
 * and none of it is identity (no session, no customer). See "Variant keying"
 * in `docs/ppr-shell.md`.
 */
export type ShellVisitor = {
  readonly request: Request;
  readonly url: URL;
  readonly siteId: string;
  /** The visitor's AB Tasty id (`hec_vid`), `""` when they have none. */
  readonly vid: string;
  /**
   * The Builder trait attributes the visitor's `hec_pz` cookie resolves to
   * (allow-listed, camelCase) — what the page lookup targets with.
   */
  readonly traits: Readonly<Record<string, string | boolean>>;
  /**
   * The campaign-flag attributes the visitor's assignments resolve to
   * (camelCase flag key → value), empty when AB Tasty is off for the site.
   */
  readonly flags: Readonly<Record<string, string>>;
};

export type ShellOptions = {
  /**
   * This route's loader fields that render OUTSIDE holes. Each is digested
   * into the shell's fingerprint; a live render whose digest differs takes
   * the regular-path reload.
   */
  fingerprint: readonly string[];
  /**
   * Per-request keys inside an otherwise shopper-independent fingerprinted
   * field, deleted before digesting (a nonce-derived stash key, for example).
   * Every key of this map must also be listed in `fingerprint`.
   */
  perRequestKeys?: Readonly<Record<string, readonly string[]>>;
  /**
   * Lifetime of the shell in the Cache API, in seconds. Default: a day
   * (`SHELL_TTL_S`). The tier's `WORKERS_PPR_SHELL_TTL_S` override, when
   * set, wins over this.
   */
  ttlSeconds?: number;
  /**
   * Lifetime of an isolate's parsed copy before the Cache API is re-read, in
   * seconds — how long a tag purge can go unnoticed. Default: a minute
   * (`SHELL_MEMORY_TTL_S`). `WORKERS_PPR_SHELL_MEMORY_TTL_S` wins over this.
   */
  memoryTtlSeconds?: number;
  /**
   * Route-specific eligibility, evaluated after every global rule
   * (`classifyPprRequest`) has passed. `true` keeps the request eligible;
   * `false` bypasses as `bypass:route`; a string bypasses with that reason.
   */
  eligible?: (request: Request, url: URL) => boolean | string;
  /**
   * Extra dimensions the shell key varies on, for a route whose served
   * content depends on something the automatic variant inputs (the Builder
   * targeting keys of the path's candidate entries) cannot see. Called at
   * the Worker seam for every eligible request and once per shell build;
   * the returned pairs (string values only, ≤ 16 keys, values clamped to
   * 64 chars) are hashed into the variant id together with the automatic
   * inputs. Return `{}` to add nothing. Must be pure and cheap: it runs
   * before the first byte. See "Variant keying" in `docs/ppr-shell.md`.
   */
  vary?: (visitor: ShellVisitor) => Record<string, string>;
};

const SHELL_KIND = "hec-document-shell";

/** What `defineShell` returns: the validated options, branded. */
export type ShellConfig = Readonly<ShellOptions> & {
  readonly kind: typeof SHELL_KIND;
};

/** A route that declares a shell, as resolved at the Worker seam. */
export type ShellRoute = {
  /** React Router's route id (`app/routes.ts`), e.g. `routes/index`. */
  routeId: string;
  config: ShellConfig;
};

/**
 * Validates and brands a route's shell declaration. Throws on a malformed
 * one: the declaration is a literal in the route module, so a mistake fails
 * the module's first import (tests, dev server) rather than a shopper's
 * request.
 */
export function defineShell(options: ShellOptions): ShellConfig {
  if (!Array.isArray(options.fingerprint)) {
    throw new TypeError("[ppr] defineShell: `fingerprint` must be an array");
  }
  const seen = new Set<string>();
  for (const field of options.fingerprint) {
    if (typeof field !== "string" || field === "" || field.includes(":")) {
      throw new TypeError(
        `[ppr] defineShell: fingerprint field ${JSON.stringify(field)} must be a non-empty loader field name`,
      );
    }
    if (seen.has(field)) {
      throw new TypeError(
        `[ppr] defineShell: fingerprint field "${field}" listed twice`,
      );
    }
    seen.add(field);
  }
  for (const [field, keys] of Object.entries(options.perRequestKeys ?? {})) {
    if (!seen.has(field)) {
      throw new TypeError(
        `[ppr] defineShell: perRequestKeys names "${field}", which is not a fingerprint field`,
      );
    }
    if (!Array.isArray(keys) || keys.some(key => typeof key !== "string")) {
      throw new TypeError(
        `[ppr] defineShell: perRequestKeys["${field}"] must be an array of key names`,
      );
    }
  }
  for (const name of ["ttlSeconds", "memoryTtlSeconds"] as const) {
    const value = options[name];
    if (value !== undefined && !(Number.isFinite(value) && value > 0)) {
      throw new TypeError(
        `[ppr] defineShell: \`${name}\` must be a positive number of seconds`,
      );
    }
  }
  if (
    options.eligible !== undefined &&
    typeof options.eligible !== "function"
  ) {
    throw new TypeError("[ppr] defineShell: `eligible` must be a function");
  }
  if (options.vary !== undefined && typeof options.vary !== "function") {
    throw new TypeError("[ppr] defineShell: `vary` must be a function");
  }
  return Object.freeze({ ...options, kind: SHELL_KIND });
}

/** Whether a route module's `shell` export came from `defineShell`. */
export function isShellConfig(value: unknown): value is ShellConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === SHELL_KIND &&
    Array.isArray((value as { fingerprint?: unknown }).fingerprint)
  );
}
